import { NgClass } from '@angular/common';
import { Component, computed, effect, ElementRef, inject, input, signal } from '@angular/core';
import { MatTooltip } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { EntityStatus, TimesPipe } from '@core';
import { FloorSpaceModel, locationUtils, Space } from '@locations';
import { NoDataPlaceholderComponent } from '@ui';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';

import { WidgetBuildingPlan } from '../../../models';
import { DashboardStore } from '../../../stores';

@Component({
  imports: [
    NgxSkeletonLoaderModule,
    NoDataPlaceholderComponent,
    RouterLink,
    MatTooltip,
    NgClass,
    TimesPipe,
  ],
  selector: 'avs-building-plan',
  styleUrls: ['./building-plan.component.scss'],
  templateUrl: './building-plan.component.html',
})
export class BuildingPlanComponent {
  config = input.required<WidgetBuildingPlan>();

  protected EntityStatus = EntityStatus;
  protected elRef = inject(ElementRef);
  protected floorList = signal<FloorSpaceModel[]>([]);
  protected roomFitsNumber = 0;

  private store = inject(DashboardStore);
  private spacesKey = signal('');
  private spacesState = computed(() => this.store.spacesList(this.spacesKey())());

  constructor() {
    effect(() => {
      if (this.config().locationId) {
        this.spacesKey.set(this.store.loadSpacesList(this.config().locationId));
      }
    });

    effect(() => {
      const spaces = this.spacesState();

      if (!spaces) {
        return;
      }

      this.floorList.set(this.normalizeSpaceResponse(spaces));
      this.calculateRoomFitsNumber();
    });
  }

  protected calculateRoomFitsNumber() {
    const containerWidth = this.elRef.nativeElement.querySelector('.rooms-container')?.offsetWidth;
    const containerMargin = 16;
    const elementMargin = 4;
    const elementWidth = 24;
    const maxRoomCount = this.floorList().reduce((acc, floor) => {
      if (floor.rooms?.length) {
        const filteredRooms = floor.rooms.filter(room => room.status !== EntityStatus.Archived);

        return filteredRooms?.length > acc ? filteredRooms?.length : acc;
      }

      return acc;
    }, 0);

    if (containerWidth) {
      this.roomFitsNumber = Math.round((containerWidth - containerMargin) / (elementWidth + elementMargin));
      this.roomFitsNumber = this.roomFitsNumber > maxRoomCount ? this.roomFitsNumber : maxRoomCount;
    }
  }

  protected normalizeSpaceResponse(spaces: Space[]) {
    const tempSpaces: Record<string, FloorSpaceModel> = {};
    const rooms = spaces.filter(locationUtils.isRoom);
    const floors = spaces.filter(locationUtils.isFloor);

    floors.forEach(floor => {
      tempSpaces[floor.id] = floor;
    });
    rooms.forEach(room => {
      if (room.parentSpaceId) {
        tempSpaces[room.parentSpaceId].rooms = [...tempSpaces[room.parentSpaceId].rooms || [], room];
      }
    });

    return Object.values(tempSpaces) as FloorSpaceModel[];
  }
}
